import { Checkbox, Image, Tooltip } from '@timelyapp/react';
import classNames from 'classnames';
import DiagnosticResourceId from 'components/dev-tools/DiagnosticResourceId';
import { EmptyUserAvatar, Lock, SmallDownArrow, SmallRightArrow } from 'components/Icons';
import RichTooltip from 'components/RichTooltip';
import { COLUMN_DATE, COLUMN_TIMESTAMPS } from 'constants/filters';
import React from 'react';
import type { GenericWidgetRow } from 'redux/modules/superreports.types';
import { prefixHexColor } from 'utils/colors';
import { createHourNotes, TIMESTAMP_TOOLTIP_CUTOFF } from 'utils/superreports';
import styles from '../TableWidget.module.scss';

interface Props {
  canSelect?: boolean;
  data: GenericWidgetRow;
  expandedState: boolean | null;
  hasBatchSelect?: boolean;
  hideCheckbox?: boolean;
  hourColumns?: Array<string>;
  isHour?: boolean;
  isLoading?: boolean;

  /**
   * Whether the cell is currently selected. `null` means the selection is indeterminate.
   */
  selectedState?: boolean | null;

  onToggleSelection?: () => void;
}

/**
 * Prevents mouse clicks from bubbling up to the table row, which would trigger the edit hour modal
 * when trying to click the selection checkbox.
 */
const stopPropagation = (event: React.MouseEvent) => {
  event.stopPropagation();
};

const TableWidgetPrimaryBlock: React.FC<Props> = ({
  canSelect,
  data,
  expandedState,
  hasBatchSelect,
  hideCheckbox,
  hourColumns = [],
  isHour,
  selectedState,
  onToggleSelection,
  isLoading,
}) => {
  let icon: React.ReactNode = null;
  if (data.avatar) {
    const altText = `Avatar for ${data.name}`;
    icon = (
      <Image
        src={data.avatar}
        className={styles.avatar}
        alt={altText}
        fallback={<EmptyUserAvatar className={styles.avatar} />}
      />
    );
  } else if (data.emoji) {
    const altText = `Emoji for ${data.name}`;
    icon = (
      <Image
        src={data.emoji}
        className={styles.emoji}
        alt={altText}
        fallback={<div className={styles.emoji} />}
      />
    );
  } else if (data.color) {
    icon = <span className={styles.color} style={{ background: prefixHexColor(data.color) }} />;
  }

  const { additionalTimestamps, hourNote } = createHourNotes(data, {
    includesDate: hourColumns.includes(COLUMN_DATE),
    includesTimestamps: hourColumns.includes(COLUMN_TIMESTAMPS),
    cutoff: TIMESTAMP_TOOLTIP_CUTOFF,
  });

  const isArchived =
    (data.resource === 'project' && data.hours?.every(h => h.project?.active === false)) ||
    (data.resource === 'hour' && data.project?.active === false);
  const isDisabled = isArchived || isLoading || hideCheckbox;

  return (
    <>
      {expandedState !== null && (
        <span className={styles.expandArrow}>
          {expandedState === false ? <SmallRightArrow /> : <SmallDownArrow />}
        </span>
      )}
      {(canSelect || hasBatchSelect) && (
        <Tooltip
          tooltip={isArchived ? 'Restore archived project to edit entry' : null}
          usePortal
          childrenClassName={styles.archivedProjectTooltip}
        >
          <Checkbox
            className={classNames(styles.checkbox, { [styles.hidden]: hideCheckbox })}
            onClick={stopPropagation}
            checked={!!selectedState}
            indeterminate={selectedState === null}
            onChangeChecked={onToggleSelection}
            disabled={isDisabled}
          />
        </Tooltip>
      )}
      {icon}
      {!isHour && (
        <span className={styles.text} title={data.name}>
          {!!data.resource && <DiagnosticResourceId resource={data.resource} id={data.id} />}
          {data.name}
        </span>
      )}
      {isHour && (
        <div className={styles.hourWrapper}>
          <div className={styles.name}>
            <span className={styles.text} title={data.name}>
              <DiagnosticResourceId resource="hour" id={data.id} />
              <DiagnosticResourceId resource="project" id={data.project?.id} />
              {data.name}
            </span>
          </div>
          <div className={styles.dateTimeRow}>
            {additionalTimestamps.length > 0 ? (
              <RichTooltip
                tooltip={
                  <ul style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {additionalTimestamps.map(_note => (
                      <li key={_note}>{_note}</li>
                    ))}
                  </ul>
                }
              >
                <div className={styles.dateTime}>{hourNote}</div>
              </RichTooltip>
            ) : hourNote.length > 0 ? (
              <div className={styles.dateTime}>{hourNote}</div>
            ) : null}
            {data.isDayLocked && (
              <Tooltip tooltip="Day is locked">
                <Lock className={styles.lock} />
              </Tooltip>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default TableWidgetPrimaryBlock;
